import { application } from "../shared/application";
import { registerControllers } from "stimulus-vite-helpers";

// Helper function to flatten controller paths in order to avoid
// having to reference controllers with the folder prefix.
//
// e.g.
//<div data-controller="toast">
// instead of
// <div data-controller="shared--toast">
//
// This is needed because we're not doing:
//
// import "../shared/shared";
//
// in the landing entrypoint, which would import all shared controllers without
// the need to flatten the paths, because shared/controllers/index.js imports
// files relative to the shared folder, so they don't have any other folder
// as a prefix.
const flattenControllerPaths = (controllers) => {
  return Object.entries(controllers).reduce((acc, [path, module]) => {
    const flattenedPath = "./" + path.split("/").pop();
    acc[flattenedPath] = module;
    return acc;
  }, {});
};

const localControllers = import.meta.glob("./**/*_controller.js", {
  eager: true,
});
// We purposely don't import shared controllers here because the shared
// controllers folder is only meant between users and admin namespaces.
// We want the landing as light as possible.
// const sharedControllers = import.meta.glob("../shared/*_controller.js", {
//   eager: true,
// });
// Only import the toast controller for the landing page.
const toastController = import.meta.glob("../shared/toast_controller.js", {
  eager: true,
});

const modalController = import.meta.glob("../shared/modal_controller.js", {
  eager: true,
});

const modalToggleController = import.meta.glob(
  "../shared/modal_toggle_controller.js",
  {
    eager: true,
  }
);

const ahoyController = import.meta.glob("../shared/ahoy_controller.js", {
  eager: true,
});

const liveChatController = import.meta.glob(
  "../shared/live_chat_controller.js",
  {
    eager: true,
  }
);

// Used by the public Restaurant Audit form at
// /grade. The autocomplete is only mounted on landing
// (no admin/users surfaces), so it's wired in here
// without polluting other entrypoints.
const placesAutocompleteController = import.meta.glob(
  "../shared/places_autocomplete_controller.js",
  {
    eager: true,
  }
);

// Used by the Restaurant Audit Step 1 visual at
// /grade/:slug to render the Carto Positron map with
// the venue + competitor pins. Same logic as
// placesAutocomplete: needed only on landing.
const locatingMapController = import.meta.glob(
  "../shared/locating_map_controller.js",
  {
    eager: true,
  }
);

// Used by the Restaurant Audit Step 2 visual to
// render insight chips and review cards from
// dataset values. Same dataset-driven architecture
// as locating-map: the canvas is rendered once on
// page load and the controller fills in the data
// when narration arrives via `set_dataset_attributes`.
const readingReviewsCanvasController = import.meta.glob(
  "../shared/reading_reviews_canvas_controller.js",
  {
    eager: true,
  }
);

// Used by the Restaurant Audit Step 3 visual to
// render the winner photo card, score breakdown,
// and discarded thumbnails grid from dataset values.
// Same dataset-driven architecture as the other
// CAL-264 step canvases.
const pickingPhotoCanvasController = import.meta.glob(
  "../shared/picking_photo_canvas_controller.js",
  {
    eager: true,
  }
);

// Used by the Restaurant Audit Step 4 visual to
// render the storefront image, swatches, typography
// preview, and tone words from dataset values. Same
// dataset-driven architecture as locating-map and
// reading-reviews-canvas.
const samplingBrandCanvasController = import.meta.glob(
  "../shared/sampling_brand_canvas_controller.js",
  {
    eager: true,
  }
);

// Used by the Restaurant Audit Step 5 visual: the
// rule-based health report (score, buckets, problem
// cards, leaderboard, CTA). The canvas renders once
// on page load and dataset broadcasts update its
// values; the controller fills the visible DOM in
// place. Same dataset-driven architecture as the
// other CAL-264 step canvases.
const readingMenuCanvasController = import.meta.glob(
  "../shared/reading_menu_canvas_controller.js",
  {
    eager: true,
  }
);

// Used by the Restaurant Audit Step 6 visual: the
// painted demo. The canvas renders the skeleton +
// painted slots once on page load; dataset broadcasts
// flip `done` and ship pre-rendered painted HTML.
// The controller injects the HTML and toggles
// visibility. Same architecture as the other CAL-264
// step canvases.
const paintingDemoCanvasController = import.meta.glob(
  "../shared/painting_demo_canvas_controller.js",
  {
    eager: true,
  }
);

// Streaming-reveal controllers used by the audit
// step visuals: GSAP-backed entrance, count-up,
// bar fill, and image-load skeleton swap. Same
// landing-only inclusion as the other audit
// controllers above.
const auditRevealController = import.meta.glob(
  "../shared/audit_reveal_controller.js",
  { eager: true }
);
const auditCountUpController = import.meta.glob(
  "../shared/audit_count_up_controller.js",
  { eager: true }
);
const auditBarFillController = import.meta.glob(
  "../shared/audit_bar_fill_controller.js",
  { eager: true }
);
const auditImageLoadController = import.meta.glob(
  "../shared/audit_image_load_controller.js",
  { eager: true }
);

// Owns visible-stage selection and pacing on the
// audit show page. Reads `set_dataset_attributes`
// broadcasts off the audit's Turbo Stream and
// advances the visible step based on its own
// wall-clock, not on what the server thinks is
// "current." See controller file header.
const auditStageController = import.meta.glob(
  "../shared/audit_stage_controller.js",
  { eager: true }
);

// Mirrors per-step status broadcasts onto the rail
// (left-column transcript) so the timeline
// indicators flip live. Lives alongside the stage
// controller: same broadcast channel, same
// dataset keys with `auditRail*` prefix.
const auditRailController = import.meta.glob(
  "../shared/audit_rail_controller.js",
  { eager: true }
);

// Yellow Continuar button (top-right of the stage
// area). Listens for `audit-stage:transition` events,
// enables/disables/hides itself based on the active
// step, and dispatches `audit-continue:click` when
// pressed so audit-stage advances to the next step.
const auditContinueButtonController = import.meta.glob(
  "../shared/audit_continue_button_controller.js",
  { eager: true }
);

// Mobile-only bottom card on the audit show page:
// progress bar, spinner, per-step Spanish headline,
// ETA countdown. Listens for `audit-stage:transition`
// to swap headline + reset countdown each step.
const auditScanningCardController = import.meta.glob(
  "../shared/audit_scanning_card_controller.js",
  { eager: true }
);

const controllers = {
  ...localControllers,
  ...flattenControllerPaths(toastController),
  ...flattenControllerPaths(modalController),
  ...flattenControllerPaths(modalToggleController),
  ...flattenControllerPaths(ahoyController),
  ...flattenControllerPaths(liveChatController),
  ...flattenControllerPaths(
    placesAutocompleteController
  ),
  ...flattenControllerPaths(locatingMapController),
  ...flattenControllerPaths(
    readingReviewsCanvasController
  ),
  ...flattenControllerPaths(
    pickingPhotoCanvasController
  ),
  ...flattenControllerPaths(
    samplingBrandCanvasController
  ),
  ...flattenControllerPaths(
    readingMenuCanvasController
  ),
  ...flattenControllerPaths(
    paintingDemoCanvasController
  ),
  ...flattenControllerPaths(auditRevealController),
  ...flattenControllerPaths(auditCountUpController),
  ...flattenControllerPaths(auditBarFillController),
  ...flattenControllerPaths(
    auditImageLoadController
  ),
  ...flattenControllerPaths(auditStageController),
  ...flattenControllerPaths(auditRailController),
  ...flattenControllerPaths(
    auditContinueButtonController
  ),
  ...flattenControllerPaths(
    auditScanningCardController
  ),
};

registerControllers(application, controllers);
